import dayjs from 'dayjs';
import * as inflection from 'inflection';
import { forwardRef } from 'react';
import Swal from 'sweetalert2';
import * as yup from 'yup';
import strings from '../Globalization/index';
import FormDialog from '../components/form';
import FormBase from '../components/form-base';
import GridBase from '../components/grid';
import GridListBase from '../components/grid/list';
import consts from '../consts';
import { default as util, default as utils } from '../util';
import dateUtil from '../util/dateUtil';
import appConfig from '../config/appConfig';

const lookupTypes = [];
const models = {};
const apiDefs = {
	base: 'Controllers/{api}.ashx'
};

//Override yup email validation, to capture emails like test.mail@stream4.tech
yup.addMethod(yup.string, 'email', function validateEmail(message) {
	return this.matches(
		/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
		{
			message,
			name: 'email',
			excludeEmptyString: true
		}
	);
});
// todo: Based on settings
const calculateFileSizeKb = (base64Value) => {
	let padding;
	if (base64Value.endsWith('==')) {
		padding = 2;
	} else if (base64Value.endsWith('=')) {
		padding = 1;
	} else {
		padding = 0;
	}
	const base64StringLength = base64Value.length;
	const inBytes = (base64StringLength / 4) * 3 - padding;
	const kbytes = inBytes / 1000;
	return kbytes;
};
const dateTimeValueFormatter =
	({ skipTime, isLocal, showSecond, format, type }) =>
		(value) => {
			if (value) {
				value = dateUtil.parse(value);
				if (isLocal) {
					value = dateUtil.getLocalDateTime(value);
				}
				let dateTimeFormat = utils.getDateTimeFormat(showSecond);
				let date = dayjs(value);
				if (date.isValid()) {
					let defaultFormat =
						skipTime || type === 'date'
							? dateTimeFormat.date
							: type === 'time'
								? dateTimeFormat.time
								: dateTimeFormat.datetime;
					value = date.format(typeof format === 'string' ? format : defaultFormat).toString();
				}
			}
			return value;
		};
const renderers = {
	date: dateTimeValueFormatter,
	datetime: dateTimeValueFormatter,
	time: dateTimeValueFormatter
};

const compareValidatorRegex = /^compare:(.+)$/;

const evalConditionalValidationConfig = ({ key, column, config, message, activeRecord, controlValue }) => {
	const checkOperandValue = ({ values, check }) => {
		let field, op, controlValue;
		let evaluatedVal = false;
		if (Array.isArray(check)) {
			field = check[0];
			controlValue = check[1];
		} else if (typeof check === 'object') {
			field = check.field;
			controlValue = check.value;
			op = check.op || check.operand;
		} else if ( typeof check === 'function' ) {
			return check({ values, activeRecord });
		}
		const value = !!values && values[field];
		if (typeof controlValue === 'function') {
			controlValue = controlValue({ value: value, values, activeRecord });
		}
		if (typeof op === 'function') {
			evaluatedVal = op({ value: value, values, activeRecord });
		}
		else {
			op = op?.trim?.()?.toLowerCase?.() || 'eq';
			switch (op) {
				case 'ne':
				case 'notequal':
					evaluatedVal = controlValue !== value;
					break;
				case 'in':
				case 'includes':
				case 'has':
					if (Array.isArray(controlValue)) {
						evaluatedVal = controlValue.indexOf(value) !== -1;
					} else if (typeof controlValue === 'object') {
						evaluatedVal =
							typeof controlValue.has === 'function' ? controlValue.has(value) : value in controlValue;
					} else if (typeof controlValue === 'string') {
						evaluatedVal = controlValue.includes(value);
					}
					break;
				case 'nin':
				case 'notin':
					if (Array.isArray(controlValue)) {
						evaluatedVal = controlValue.indexOf(value) === -1;
					} else if (typeof controlValue === 'object') {
						evaluatedVal =
							typeof controlValue.has === 'function' ? !controlValue.has(value) : !(value in controlValue);
					} else if (typeof controlValue === 'string') {
						evaluatedVal = !controlValue.includes(value);
					}
					break;
				case 'lt':
				case 'lessthan':
					evaluatedVal = value < controlValue;
					break;
				case 'lte':
				case 'lessthanorequal':
					evaluatedVal = value <= controlValue;
					break;
				case 'gt':
				case 'greaterthan':
					evaluatedVal = value > controlValue;
					break;
				case 'gte':
				case 'greaterthanorequal':
					evaluatedVal = value >= controlValue;
					break;
				case 'or':
					evaluatedVal =
						evaluatedVal || Array.isArray(controlValue)
							? controlValue.some((orCheck) => checkOperandValue({ values, check: orCheck }))
							: evaluatedVal;
					break;
				case 'and':
					evaluatedVal = Array.isArray(controlValue)
						? controlValue.every((andCheck) => checkOperandValue({ values, check: andCheck }))
						: evaluatedVal;
					break;
				default:
					evaluatedVal = value === controlValue;
					break;
			}
		}


		return evaluatedVal;
	};

	if (typeof column === 'object' && key in column && Array.isArray(column[key])) {
		const name = `${column.formField || column.field}-${key}`;
		config = config.test({
			name: name.toLowerCase(),
			message: message || key,
			test: (value, { parent, schema }) => {
				let validate = column[key].every((check) => checkOperandValue({ check, values: parent }));
				let isValid = true;
				const checkRequiredValueValid = (value) => {
					if ('type' in column) {
						return value === 0 ? true : Boolean(value);
					}
					return column.multiple ? value?.length > 0 : value !== undefined && value !== null && value?.trim?.() !== '';
				};
				if (validate) {
					switch (key) {
						case 'required':
							isValid = checkRequiredValueValid(value);
							break;
						case 'min':
							isValid = value >= column.minValue;
							break;
						case 'max':
							isValid = value <= column.maxValue;
							break;
						default:
							break;
					}
				}

				return isValid;
			}
		});
	}

	return config;
};

const getValidationSchema =
	({ modelConfig }) =>
		({ activeRecord }, formRef) => {
			const validationConfig = {};
			const { columns } = modelConfig;

			for (const column of columns) {
				let {
					label,
					field,
					header,
					formField,
					type = 'string',
					required = false,
					minLength,
					maxLength,
					multiple,
					validate
				} = column;
				if (formField) {
					field = formField;
				}
				if (typeof minLength === 'function') {
					minLength = minLength({ activeRecord });
				}
				if (typeof required === 'function') {
					required = required({ activeRecord });
				}
				if (typeof maxLength === 'function') {
					maxLength = maxLength({ activeRecord });
				}
				if (typeof validate === 'function') {
					validate = validate({ activeRecord });
				}
				let config;
				const fieldLabelName = label || header || field;
				const fieldLabel = strings[fieldLabelName] || fieldLabelName;
				if (!fieldLabel) {
					continue;
				}
				if ((type === 'string' || type === 'password') && !multiple) {
					config = yup.string().nullable();
					let passwordRegExp = consts.PasswordStrength.Strong;
					let passwordValidationError = strings.PasswordValidationError;
					const skipPasswordStrengthCheck =
						typeof column.skipPasswordStrengthCheck === 'function'
							? column.skipPasswordStrengthCheck({ activeRecord })
							: column.skipPasswordStrengthCheck;
					if (type === 'password' && !skipPasswordStrengthCheck && !('validate' in column) && !multiple) {
						passwordRegExp = util.getPasswordRegExp('PasswordStrength');
						passwordValidationError =
							passwordRegExp === consts.PasswordStrength.Easy
								? strings.PasswordEasyValidationError
								: passwordRegExp === consts.PasswordStrength.Medium
									? strings.PasswordMediumValidationError
									: strings.PasswordValidationError;
						config = config.matches(passwordRegExp, ` ${passwordValidationError}`);
					}

					if (typeof required === 'boolean' && required) {
						config = config.strict(true).required('requiredField');
					}
					if (typeof minLength === 'number') {
						config = config.min(minLength);
					}
					if (typeof maxLength === 'number') {
						config = config.max(maxLength);
					}
					if (validate) {
						switch (validate) {
							case 'isEmail':
								config = config.email(strings?.EmailIsInvalid ?? 'EmailIsInvalid');
								break;
							case 'isUrl':
								config = config.test(
									'is-url',
									typeof strings.URLValidation === 'string' ? strings.URLValidation : 'Invalid URL',
									value => {
										if (!value) return true; // If no value, skip validation
										return /^(https?:\/\/)((localhost)|((\d{1,3}\.){3}\d{1,3})|([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(:\d{1,5})?(\/[^\s]*)?(\?[^\s#]*)?(#[^\s]*)?$/.test(value);
									}
								);
								break;
							case 'isNumeric':
								config = config.number();
								break;
							default:
								const compareValidator = compareValidatorRegex.exec(validate);
								if (compareValidator) {
									const compareFieldName = compareValidator[1];
									const compareField = columns.find(
										(f) => (f.formField === compareFieldName || f.field) === compareFieldName
									);
									config = config.oneOf(
										[yup.ref(compareFieldName)],
										` must match ${compareField.label
											? strings[compareField.label] || compareField.label
											: strings[compareField.header] || compareField.header
										}`
									);
								}
						}
					}
				} else if (type === 'number') {
					config = yup.number();
					config = config.transform((value, originalValue) => {
						return !isNaN(value) ? value : 0;
					});
					if (typeof required === 'boolean' && required) {
						config = config.required('requiredField');
					}
					if (column.decimal !== true) {
						config = config.integer();
					}
					if (typeof column.minValue === 'number' && !Array.isArray(column.min)) {
						config = config.min(column.minValue, `{0} minimum is ${column.minValue}`);
					}
					if (typeof column.maxValue === 'number' && !Array.isArray(column.max)) {
						config = config.max(column.maxValue, `{0} maximum is ${column.maxValue}`);
					}
				} else if (['date', 'time', 'datetime'].includes(type)) {
					config = yup.mixed();
					if (typeof required === 'boolean' && required) {
						config = config.test('is-a-dayjs-parceable', 'requiredField', (value) => {
							const valid = !!value && dayjs(value).isValid();
							return valid;
						});
					}
					if (column.maxDate || column.minDate) {
						// To Do:-
					}
				} else if (type === 'file') {
					config = yup.mixed().label(fieldLabel).transform((v) => (!v ? undefined : v));
					if (typeof required === 'boolean' && required) {
						config = config.required('requiredField');
					}
					if (typeof column.maxSize === 'number' || typeof column.minSize === 'number') {
						config = config.test('is-file-size-valid', ` ${strings.ImageWarning}`, (value) => {
							let fileSize = 0;
							let valid = true;
							if (typeof value === 'string' || value instanceof File) {
								if (value instanceof File) {
									fileSize = value.size || 0;
								} else {
									fileSize = calculateFileSizeKb(value);
								}
								valid =
									(column.minSize > 0 && fileSize > column.minSize) ||
									(column.maxSize > 0 && fileSize < column.maxSize);
								if (!valid) {
									Swal.fire({ titleText: strings.Alert, text: strings.ImageWarning, icon: 'warning', confirmButtonText: strings.Ok, allowOutsideClick: false });
								}
							}
							
							return valid;
						});
					}
				}

				// This needs checking if it would to undesired changes
				if (multiple) {
					if (column.type !== 'string') {
						config = yup.array().nullable();
					}
					if (typeof required === 'boolean' && required && config) {
						config = config.test(`${field}-has-atleast-one-of-multiple`, 'requiredField', (value) => {
							let testVal = value;
							if (typeof value === 'string') {
								try {
									testVal = value?.startsWith('[') && value?.endsWith(']') ? JSON.parse(value) : value.split(',');
								} catch (error) {
									testVal = value?.trim?.();
								}

							}
							return testVal?.length > 0;
						});
					}
				}

				if (typeof validate === 'object') {
					config = validate;
				}
				if (Array.isArray(required)) {
					config = evalConditionalValidationConfig({
						key: 'required',
						column: { ...column, required },
						config,
						message: 'requiredField',
						activeRecord
					});
				}
				if (Array.isArray(column.min)) {
					config = evalConditionalValidationConfig({
						key: 'min',
						column,
						config,
						message: `{0} minimum is ${column.minValue}`,
						activeRecord
					});
				}
				if (Array.isArray(column.max)) {
					config = evalConditionalValidationConfig({
						key: 'max',
						column,
						config,
						message: `{0} maximum is ${column.maxValue}`,
						activeRecord
					});
				}
				if (config) {
					validationConfig[field] = config;
				}
			}

			return yup.object(validationConfig);
		};

class Model {
	static lookups = {};

	static registerLookup({ name, displayField }) {
		Model.lookups[name] = displayField;
	}

	static getLookup(name) {
		return Model.lookups[name];
	}

	constructor(config) {
		this.init(config);
	}

	init(config) {
		const { name } = config;
		config = {
			title: strings[name],
			controllerType: config.api ? 'rest' : 'default',
			api: name,
			type: 'string',
			idProperty: name + 'Id',
			defaultSort: name + 'Id',
			listTitle: inflection.pluralize(name),
			...config
		};

		const { defaultSort, readOnly, linkColumn } = config;
		if (!readOnly && linkColumn === undefined && defaultSort) {
			if (typeof defaultSort === 'string') {
				config.linkColumn = defaultSort.split(' ')[0];
			} else {
				config.linkColumn = defaultSort[0].field;
			}
		}

		const { api } = config;
		if (!config.endPoint) {
			const endPoint = api || name;
			const relativeEndPoint = endPoint.startsWith('/') ? endPoint : utils.replaceTags(apiDefs.base, { api });
			// Create absolute URL using appConfig.ApiUrl
			const baseUrl = appConfig.ApiUrl || "http://localhost:8082";
			config.endPoint = baseUrl + '/' + relativeEndPoint;
		}
		Object.assign(this, config);

		const formConfig = this.createForm({ modelConfig: config });

		Object.assign(this, formConfig);

		Model.registerLookup({ name: this.name, displayField: this.displayField });
	}

	createForm({ modelConfig }) {
		const validationConfig = {},
			initialValues = {},
			formDef = [];
		const { idProperty } = modelConfig;
		for (const column of modelConfig.columns) {
			// TODO: handle requiredIfNew
			let { label, header, type = 'string', formType, isNotformfield, multiple } = column;
			let { field, formField } = column;
			if (formType) {
				type = formType;
			}
			if (formField) {
				field = formField;
			}
			if (isNotformfield || label === null || label === '') {
				continue;
			}
			if (field === idProperty) {
				continue;
			}
			if (type === 'action') {
				continue;
			}
			let { defaultValue } = column;
			const fieldLabelName = label || header || field;
			const fieldLabel = strings[fieldLabelName] || fieldLabelName;
			if (!fieldLabel) {
				continue;
			}
			if (multiple) {
				defaultValue = defaultValue || [];
			}
			if (
				typeof defaultValue === 'undefined' &&
				typeof modelConfig.initialValues === 'object' &&
				field in modelConfig.initialValues
			) {
				defaultValue =
					type === 'boolean' || type === 'bool'
						? Boolean(modelConfig.initialValues[field])
						: modelConfig.initialValues[field];
			} else if ((type === 'boolean' || type === 'bool') && typeof defaultValue !== 'boolean') {
				defaultValue = false;
			} else if (type === 'string') {
				defaultValue = defaultValue?? '';
			} else if (type === 'password') {
				defaultValue = defaultValue?? '';
			}
			if (type === 'number' && !('defaultValue' in column) && typeof defaultValue !== 'number') {
				defaultValue = 0;


			}

			column.label = fieldLabel;
			column.fieldLabelName = fieldLabelName;
			column.name = column.formField || column.field;
			initialValues[field] = defaultValue;
			formDef.push({ ...column, type, field });
		}

		return {
			validationSchema: yup.object(validationConfig),
			getValidationSchema: getValidationSchema({ modelConfig }),
			initialValues,
			formDef
		};
	}

	getGridColumns(props) {
		const { columns, readOnly, linkColumn, filterColumns } = this;
		let gridColumns = [];

		const { hideColumns } = typeof filterColumns === 'function' ? filterColumns({ grid: true, ...props }) || {} : {};

		for (const column of columns) {
			// if empty header name, we don't want to see this column
			if (column.headerName === null || column.headerName === '') {
				continue;
			}
			const headerName = column.headerName || column.header || column.field;
			if (headerName) {
				let valueGetter,
					valueFormatter = column.valueFormatter || column.type || 'string';
				if (typeof valueFormatter === 'string') {
					valueFormatter = valueFormatter.toLowerCase();
				}
				if (['date', 'datetime', 'time'].includes(valueFormatter)) {
					valueFormatter = renderers[valueFormatter](column);
				} else if (typeof valueFormatter === 'string') {
					valueFormatter = renderers[valueFormatter];
				}
				if (column.type === 'int') {
					column.type = 'number'
				}

				gridColumns.push({
					valueFormatter,
					valueGetter,
					cellClassName: readOnly !== true && column.field === linkColumn ? 'hyperlink' : undefined,
					headerName: column.header?.trim().length === 0 ? '' : strings[headerName] || headerName,
					sortable: true,
					filter: true,
					hide: column.hidden,
					...column,
					type: column.type === 'bool' ? 'boolean' : column.type
				});
			}
		}
		if (hideColumns?.length) {
			gridColumns = gridColumns.filter((c) => !hideColumns.includes(c.field));
		}
		return gridColumns;
	}

	createGridBase() {
		const model = this;
		return forwardRef((props, ref) => {
			return <GridBase modelConfig={model} readOnly={true} {...props} ref={ref} />;
		});
	}

	createGridListBase() {
		const model = this;
		return (props) => {
			return <GridListBase modelConfig={model} readOnly={true} {...props} />;
		};
	}

	createFormBase() {
		const model = this;
		return (props) => {
			return <FormBase modelConfig={model} {...props} />;
		};
	}

	createFormDialog() {
		const model = this;
		return forwardRef((props, ref) => {
			return <FormDialog modelConfig={model} {...props} ref={ref} />;
		});
	}
}

export { apiDefs, lookupTypes, models };
export default Model;
